import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import { CheckCircle, X, Camera, Home, Sparkles, ArrowRight } from 'lucide-react';
import styles from './SpotlightQuestionnaire.module.sass';
import InfoPage from '../InfoPage';
import { getAuthToken } from '../../utils/authHelpers';
import { uploadFile } from '../../utils/fileUpload';
import {
  SPOTLIGHT_SECTIONS,
  getSpotlightQuestions,
  isSpotlightQuestionVisible,
} from '../../data/spotlightQuestions';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_IMAGE_BYTES = 20 * 1024 * 1024;
const MAX_PROPERTY_IMAGES = 6;
const VALID_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];

const COPY = {
  creator: {
    title: 'Creator Spotlight',
    intro: 'Each week we spotlight one Postaway creator in our newsletter and on the Creator Spotlight page. Answer the questions below (about 10 minutes) and we’ll take care of the rest.',
    photoHint: 'It doesn’t have to be professional — just one that shows your personality.',
    thanks: 'Thank you for taking the time to share a little about your creator journey! We will let you know when your spotlight is posted.',
    spotlightPath: '/creators/spotlight',
  },
  host: {
    title: 'Host Spotlight',
    intro: 'Each week we spotlight one Postaway host in our newsletter and on the Host Spotlight page. Answer the questions below (about 10 minutes) and we’ll take care of the rest.',
    photoHint: 'It doesn’t have to be professional — just one that shows your personality.',
    thanks: 'Thank you for taking the time to share a little about your hosting journey! We will let you know when your spotlight is posted.',
    spotlightPath: '/hosts/spotlight',
  },
};

const draftKey = (type) => `postaway_spotlight_draft_${type}`;

function isValidImage(file) {
  const name = (file.name || '').toLowerCase();
  return VALID_IMAGE_TYPES.includes((file.type || '').toLowerCase()) || name.endsWith('.heic') || name.endsWith('.heif');
}

const SpotlightQuestionnaire = ({ type = 'creator' }) => {
  const copy = COPY[type] || COPY.creator;
  const sections = SPOTLIGHT_SECTIONS[type] || SPOTLIGHT_SECTIONS.creator;
  const allQuestions = useMemo(() => getSpotlightQuestions(type), [type]);

  const apiBase = typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'http://localhost:4000' : '';

  // Contact
  const [name, setName] = useState('');
  const [igHandle, setIgHandle] = useState('');
  const [email, setEmail] = useState('');

  // Answers
  const [answers, setAnswers] = useState({});

  // Media
  const [headshotUrl, setHeadshotUrl] = useState('');
  const [propertyImageUrls, setPropertyImageUrls] = useState([]);
  const [uploadingHeadshot, setUploadingHeadshot] = useState(false);
  const [uploadingProperty, setUploadingProperty] = useState(false);

  // Flow
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [touched, setTouched] = useState(false);
  const [draftRestored, setDraftRestored] = useState(false);

  // Restore draft / prefill email
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const raw = window.localStorage.getItem(draftKey(type));
      if (raw) {
        const draft = JSON.parse(raw);
        setName(draft.name || '');
        setIgHandle(draft.igHandle || '');
        setEmail(draft.email || '');
        setAnswers(draft.answers || {});
        setHeadshotUrl(draft.headshotUrl || '');
        setPropertyImageUrls(Array.isArray(draft.propertyImageUrls) ? draft.propertyImageUrls : []);
        setDraftRestored(Boolean(draft.name || Object.keys(draft.answers || {}).length));
      } else {
        const storedEmail = window.localStorage.getItem('postaway_email') || '';
        if (storedEmail) setEmail(storedEmail);
      }
    } catch (_) {}
  }, [type]);

  // Persist draft
  useEffect(() => {
    if (typeof window === 'undefined' || submitted) return;
    try {
      window.localStorage.setItem(
        draftKey(type),
        JSON.stringify({ name, igHandle, email, answers, headshotUrl, propertyImageUrls })
      );
    } catch (_) {}
  }, [type, name, igHandle, email, answers, headshotUrl, propertyImageUrls, submitted]);

  const setAnswer = useCallback((id, value) => {
    setAnswers((prev) => ({ ...prev, [id]: value }));
  }, []);

  const requiredMissing = useMemo(() => {
    const missing = [];
    if (!name.trim()) missing.push('name');
    if (!EMAIL_REGEX.test(email.trim())) missing.push('email');
    if (!igHandle.trim()) missing.push('igHandle');
    if (!headshotUrl) missing.push('headshot');
    for (const q of allQuestions) {
      if (!q.required || !isSpotlightQuestionVisible(q, answers)) continue;
      const v = answers[q.id];
      if (q.type === 'list') {
        if (!Array.isArray(v) || !v.some((x) => String(x || '').trim())) missing.push(q.id);
      } else if (!v || !String(v).trim()) {
        missing.push(q.id);
      }
    }
    return missing;
  }, [name, email, igHandle, headshotUrl, answers, allQuestions]);

  const requiredTotal = useMemo(() => {
    const base = 4; // name, email, handle, headshot
    return base + allQuestions.filter((q) => q.required && isSpotlightQuestionVisible(q, answers)).length;
  }, [allQuestions, answers]);
  const requiredDone = requiredTotal - requiredMissing.length;

  const handleHeadshotChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > MAX_IMAGE_BYTES) { setError('Headshot must be under 20MB'); return; }
    if (!isValidImage(file)) { setError('Please upload a JPG, PNG, WebP, or HEIC image'); return; }
    setError('');
    setUploadingHeadshot(true);
    try {
      const url = await uploadFile(file);
      setHeadshotUrl(url);
    } catch (err) {
      console.error('Headshot upload failed:', err);
      setError('Failed to upload headshot. Please try again.');
    } finally {
      setUploadingHeadshot(false);
    }
  };

  const handlePropertyChange = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    const room = MAX_PROPERTY_IMAGES - propertyImageUrls.length;
    if (room <= 0) { setError(`You can upload up to ${MAX_PROPERTY_IMAGES} property photos`); return; }
    const batch = files.slice(0, room);
    for (const f of batch) {
      if (f.size > MAX_IMAGE_BYTES) { setError('Each property photo must be under 20MB'); return; }
      if (!isValidImage(f)) { setError('Please upload JPG, PNG, WebP, or HEIC images'); return; }
    }
    setError('');
    setUploadingProperty(true);
    try {
      const urls = [];
      for (const f of batch) {
        // sequential keeps ordering stable and avoids hammering the upload endpoint
        // eslint-disable-next-line no-await-in-loop
        urls.push(await uploadFile(f));
      }
      setPropertyImageUrls((prev) => [...prev, ...urls].slice(0, MAX_PROPERTY_IMAGES));
    } catch (err) {
      console.error('Property upload failed:', err);
      setError('Failed to upload a property photo. Please try again.');
    } finally {
      setUploadingProperty(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setTouched(true);
    if (requiredMissing.length) {
      setError('Please fill in the highlighted required fields.');
      const firstId = requiredMissing[0];
      const el = document.getElementById(`sq-${firstId}`);
      if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setError('');
    setSubmitting(true);
    try {
      const token = getAuthToken();
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      const cleanAnswers = {};
      for (const q of allQuestions) {
        if (!isSpotlightQuestionVisible(q, answers)) continue;
        const v = answers[q.id];
        if (v == null || v === '') continue;
        cleanAnswers[q.id] = q.type === 'list'
          ? (Array.isArray(v) ? v.map((x) => String(x || '').trim()) : [])
          : String(v).trim();
      }

      const response = await fetch(`${apiBase}/api/spotlight/submit`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          type,
          name: name.trim(),
          igHandle: igHandle.trim(),
          email: email.trim(),
          headshotUrl,
          propertyImageUrls: type === 'host' ? propertyImageUrls : [],
          answers: cleanAnswers,
        }),
      });
      const data = await response.json();
      if (data.ok) {
        setSubmitted(true);
        try { window.localStorage.removeItem(draftKey(type)); } catch (_) {}
        window.scrollTo?.({ top: 0, behavior: 'smooth' });
      } else {
        setError(data.error === 'invalid_email' ? 'Please enter a valid email address.' : (data.error || 'Submission failed. Please try again.'));
      }
    } catch (err) {
      console.error('Spotlight submit failed:', err);
      setError('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const clearDraft = () => {
    setName(''); setIgHandle(''); setAnswers({}); setHeadshotUrl(''); setPropertyImageUrls([]);
    setDraftRestored(false); setTouched(false); setError('');
    try {
      window.localStorage.removeItem(draftKey(type));
      setEmail(window.localStorage.getItem('postaway_email') || '');
    } catch (_) { setEmail(''); }
  };

  const showMissing = (id) => touched && requiredMissing.includes(id);

  const renderQuestion = (q) => {
    if (!isSpotlightQuestionVisible(q, answers)) return null;
    const value = answers[q.id];
    const invalid = showMissing(q.id);
    const labelEl = (
      <label className={styles.fieldLabel} htmlFor={`sq-input-${q.id}`}>
        {q.label}
        {q.required ? <span className={styles.req}> *</span> : <span className={styles.optional}> (optional)</span>}
      </label>
    );

    return (
      <div key={q.id} id={`sq-${q.id}`} className={cn(styles.fieldGroup, { [styles.invalid]: invalid })}>
        {labelEl}
        {q.hint && <p className={styles.hint}>{q.hint}</p>}

        {q.type === 'textarea' && (
          <textarea
            id={`sq-input-${q.id}`}
            className={styles.textarea}
            value={value || ''}
            placeholder={q.placeholder || ''}
            onChange={(e) => setAnswer(q.id, e.target.value)}
            rows={4}
            maxLength={4000}
          />
        )}

        {q.type === 'text' && (
          <input
            id={`sq-input-${q.id}`}
            type="text"
            className={styles.input}
            value={value || ''}
            placeholder={q.placeholder || ''}
            onChange={(e) => setAnswer(q.id, e.target.value)}
            maxLength={500}
          />
        )}

        {q.type === 'select' && (
          <select
            id={`sq-input-${q.id}`}
            className={cn(styles.input, styles.select)}
            value={value || ''}
            onChange={(e) => setAnswer(q.id, e.target.value)}
          >
            <option value="">Select one…</option>
            {(q.options || []).map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        )}

        {q.type === 'list' && (
          <div className={styles.listInputs}>
            {Array.from({ length: q.count || 3 }).map((_, i) => (
              <input
                key={i}
                id={i === 0 ? `sq-input-${q.id}` : undefined}
                type="text"
                className={styles.input}
                placeholder={`App ${i + 1}`}
                value={(Array.isArray(value) ? value[i] : '') || ''}
                onChange={(e) => {
                  const next = Array.isArray(value) ? [...value] : [];
                  next[i] = e.target.value;
                  setAnswer(q.id, next);
                }}
                maxLength={100}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  const successContent = (
    <div className={styles.page}>
      <div className={styles.container}>
        <div className={styles.successCard}>
          <CheckCircle size={44} className={styles.successIcon} />
          <h2 className={styles.successTitle}>You’re all set!</h2>
          <p className={styles.successText}>{copy.thanks}</p>
          <Link to={copy.spotlightPath} className={styles.secondaryBtn}>
            See past spotlights <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </div>
  );

  const formContent = (
    <div className={styles.page}>
      <div className={styles.container}>
        <div className={styles.introCard}>
          <div className={styles.introBadge}><Sparkles size={14} /> {copy.title} Questionnaire</div>
          <p className={styles.intro}>{copy.intro}</p>
          {draftRestored && (
            <p className={styles.draftNote}>
              We restored your in-progress answers.{' '}
              <button type="button" className={styles.linkBtn} onClick={clearDraft}>Start over</button>
            </p>
          )}
        </div>

        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          {/* About you */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>About you</h2>
            <div className={styles.row}>
              <div id="sq-name" className={cn(styles.fieldGroup, { [styles.invalid]: showMissing('name') })}>
                <label className={styles.fieldLabel} htmlFor="sq-input-name">Name <span className={styles.req}>*</span></label>
                <input id="sq-input-name" type="text" className={styles.input} value={name} onChange={(e) => setName(e.target.value)} maxLength={200} autoComplete="name" />
              </div>
              <div id="sq-igHandle" className={cn(styles.fieldGroup, { [styles.invalid]: showMissing('igHandle') })}>
                <label className={styles.fieldLabel} htmlFor="sq-input-igHandle">IG Handle <span className={styles.req}>*</span></label>
                <div className={styles.handleInput}>
                  <span className={styles.handlePrefix}>@</span>
                  <input
                    id="sq-input-igHandle"
                    type="text"
                    className={styles.handleField}
                    value={igHandle}
                    onChange={(e) => setIgHandle(e.target.value.replace(/@/g, '').replace(/\s/g, ''))}
                    maxLength={100}
                    autoComplete="off"
                  />
                </div>
              </div>
            </div>
            <div id="sq-email" className={cn(styles.fieldGroup, { [styles.invalid]: showMissing('email') })}>
              <label className={styles.fieldLabel} htmlFor="sq-input-email">Email <span className={styles.req}>*</span></label>
              <input id="sq-input-email" type="email" className={styles.input} value={email} onChange={(e) => setEmail(e.target.value)} maxLength={320} autoComplete="email" />
              <p className={styles.hint}>We’ll use this to let you know when your spotlight goes live.</p>
            </div>
          </section>

          {/* Photos */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Your photos</h2>

            <div id="sq-headshot" className={cn(styles.fieldGroup, { [styles.invalid]: showMissing('headshot') })}>
              <label className={styles.fieldLabel}>Headshot <span className={styles.req}>*</span></label>
              <p className={styles.hint}>{copy.photoHint}</p>
              {headshotUrl ? (
                <div className={styles.headshotPreview}>
                  <img src={headshotUrl} alt="Headshot preview" />
                  <button type="button" className={styles.removeBtn} onClick={() => setHeadshotUrl('')}>
                    <X size={14} /> Remove
                  </button>
                </div>
              ) : (
                <label className={cn(styles.uploadLabel, { [styles.uploading]: uploadingHeadshot })}>
                  <input type="file" accept="image/*,.heic,.heif" onChange={handleHeadshotChange} disabled={uploadingHeadshot} style={{ display: 'none' }} />
                  {uploadingHeadshot ? <span>Uploading…</span> : (
                    <>
                      <Camera size={28} />
                      <span>Click to upload your headshot</span>
                      <span className={styles.uploadHint}>JPG, PNG, WebP, or HEIC up to 20MB</span>
                    </>
                  )}
                </label>
              )}
            </div>

            {type === 'host' && (
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>Property photos <span className={styles.optional}>(optional, up to {MAX_PROPERTY_IMAGES})</span></label>
                <p className={styles.hint}>Show off your space — exterior, hero shot, favorite corner.</p>
                {propertyImageUrls.length > 0 && (
                  <div className={styles.propertyGrid}>
                    {propertyImageUrls.map((url, i) => (
                      <div key={url} className={styles.propertyThumb}>
                        <img src={url} alt={`Property ${i + 1}`} />
                        <button
                          type="button"
                          className={styles.thumbRemove}
                          aria-label="Remove photo"
                          onClick={() => setPropertyImageUrls((prev) => prev.filter((u) => u !== url))}
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {propertyImageUrls.length < MAX_PROPERTY_IMAGES && (
                  <label className={cn(styles.uploadLabel, styles.uploadLabelCompact, { [styles.uploading]: uploadingProperty })}>
                    <input type="file" accept="image/*,.heic,.heif" multiple onChange={handlePropertyChange} disabled={uploadingProperty} style={{ display: 'none' }} />
                    {uploadingProperty ? <span>Uploading…</span> : (
                      <>
                        <Home size={22} />
                        <span>{propertyImageUrls.length ? 'Add more property photos' : 'Click to upload property photos'}</span>
                      </>
                    )}
                  </label>
                )}
              </div>
            )}
          </section>

          {/* Questionnaire sections */}
          {sections.map((sec) => (
            <section key={sec.section} className={styles.section}>
              <h2 className={styles.sectionTitle}>{sec.section}</h2>
              {sec.questions.map(renderQuestion)}
            </section>
          ))}

          <div className={styles.submitArea}>
            <div className={styles.progress}>
              <div className={styles.progressBar}>
                <div className={styles.progressFill} style={{ width: `${Math.round((requiredDone / Math.max(requiredTotal, 1)) * 100)}%` }} />
              </div>
              <span className={styles.progressText}>{requiredDone} of {requiredTotal} required answers complete</span>
            </div>
            {error && <p className={styles.error}>{error}</p>}
            <button type="submit" className={styles.submitBtn} disabled={submitting || uploadingHeadshot || uploadingProperty}>
              {submitting ? 'Submitting…' : 'Submit my spotlight'}
            </button>
            <p className={styles.fineprint}>
              By submitting you give Postaway permission to share your answers and photos in our newsletter, on postaway.com, and on social media.
            </p>
          </div>
        </form>

        <div className={styles.altCard}>
          <div>
            <strong>Want your photo on our Instagram grid instead?</strong>
            <p>Submit a photo from a recent stay or collab and we’ll consider it for @postawaytravels.</p>
          </div>
          <Link to="/get-featured" className={styles.secondaryBtn}>Submit a photo <ArrowRight size={16} /></Link>
        </div>
      </div>
    </div>
  );

  return (
    <InfoPage
      title={copy.title}
      divider={false}
      below={submitted ? successContent : formContent}
      disableStamps
      tallOverride={false}
      customMinHeight={undefined}
    />
  );
};

export default SpotlightQuestionnaire;
